import { TransactionActivity } from "@models/clients/clients.details/transactionActivity";
import { TableColumn } from "@models/components/tableColumn";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { DropdownOptionsService } from "@services/clients/dropdownOptions.service";

export class ClientDetailsBankingActivitiesController {
    public client: any;
    public clientNumber: string; // Add clientNumber binding
    public isViewOnly: boolean = false;
    public transactions: any[] = [];
    public loading: boolean = true;
    
    // Dropdown options
    public dropdownOptions: any = {};
    public clientPortfolioAccounts: any[] = [];

    /** @ngInject */
    constructor(
        private $mdDialog: angular.material.IDialogService,
        private $rootScope: angular.IRootScopeService,
        private $scope: angular.IScope,
        private clientCRMRepo: ClientCRMRepo,
        private dropdownOptionsService: DropdownOptionsService
    ) {}

    public transactionColumns: TableColumn[] = [
        { key: 'name', label: 'Transaction Activity Name', type: 'text' },
        { key: 'transactionTypeName', label: 'Transaction Type', type: 'text' },
        { key: 'bankName', label: 'Bank', type: 'text' },
        { key: 'amountDisplay', label: 'Amount', type: 'currency' },
        { key: 'currencyCode', label: 'Currency', type: 'text' },
        // { key: 'nextCapitalRepaymentDate', label: 'Next Capital Repayment Date', type: 'date' }
    ];

    public $onInit(): void {
        // Component initialization logic for Dynamics 365 banking data
        // Use clientNumber for initial data loading instead of waiting for client
        if (this.clientNumber && this.clientNumber !== 'Individual' && this.clientNumber !== 'Institution') {
            this.loadTransactions();
            this.loadClientPortfolioAccounts();
        }
        
        // Load dropdown options
        this.dropdownOptionsService.getAllOptions()
            .then((options) => {
                this.loadDropdownOptions(options);
            })
            .catch((error) => {
                console.warn('Failed to get dropdown options:', error);
            });
    }


    public loadTransactions(): void {
        // Use clientNumber if available, otherwise fall back to client.id
        const clientNumber = this.clientNumber
        
        if (clientNumber) {
            this.clientCRMRepo.getTransactionActivities(clientNumber)
                .then((response: any) => {
                    this.transactions = response.data || [];
                    this.loading = false;
                })
                .catch((error) => {
                    console.error('Error loading transaction activities:', error);
                    this.loading = false;
                });
        }
    }

    public loadClientPortfolioAccounts(): void {
        // Use clientNumber if available, otherwise fall back to client.id
        const clientNumber = this.clientNumber
        
        if (clientNumber) {
            this.clientCRMRepo.getClientPortfolioAccounts(clientNumber)
                .then((response: any) => {
                    this.clientPortfolioAccounts = response.data || [];
                })
                .catch((error) => {
                    console.error('Error loading client portfolio accounts:', error);
                });
        }
    }

    public addNewTransactionActivity(): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.transactionActivity = {
            name: '',
            transactionType: undefined,
            bank: undefined,
            currency: undefined,
            amount: 0,
            portfolioAccountSelection: undefined,
            portfolioAccount: undefined
        } as Partial<TransactionActivity>;
        
        // Pass dropdown options and portfolio accounts to the modal
        tempScope.dropdownOptions = this.dropdownOptions;
        tempScope.clientPortfolioAccounts = this.clientPortfolioAccounts;

        this.$mdDialog
            .show({
                template: '<md-dialog flex="70"><add-transaction-activity-modal client="client" transaction-activity="transactionActivity" dropdown-options="dropdownOptions" client-portfolio-accounts="clientPortfolioAccounts"></add-transaction-activity-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                console.log('result', result);
                if (result) {
                    this.transactionActivitySaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening transaction activity modal:', error);
            });
    }

    public editTransactionActivity(transactionActivity: any): void {
        if (this.isViewOnly) return;
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.transactionActivity = transactionActivity;
        tempScope.dropdownOptions = this.dropdownOptions;
        tempScope.clientPortfolioAccounts = this.clientPortfolioAccounts;

        this.$mdDialog
            .show({
                template: '<md-dialog flex="70"><add-transaction-activity-modal client="client" transaction-activity="transactionActivity" dropdown-options="dropdownOptions" client-portfolio-accounts="clientPortfolioAccounts"></add-transaction-activity-modal></md-dialog>',
                scope: tempScope,
                clickOutsideToClose: true,
                escapeToClose: true
            })
            .then((result) => {
                if (result) {
                    this.transactionActivitySaved(result);
                }
            })
            .catch((error) => {
                console.error('Error opening transaction activity modal:', error);
            });
    }

    public deleteSelectedTransactionActivities(itemIds: number[]): angular.IPromise<void> | void {
        if (itemIds.length === 0) {
            return;
        }

        const confirm = this.$mdDialog.confirm()
            .title('Delete Transaction Activities')
            .textContent(`Are you sure you want to delete ${itemIds.length} transaction activit(ies)?`)
            .ok('Delete')
            .cancel('Cancel');

        return this.$mdDialog.show(confirm).then(() => {
            this.loading = true;
            return this.clientCRMRepo.deleteTransactionActivities(itemIds)
                .then(() => {
                    this.transactions = this.transactions.filter(item => !itemIds.includes(item.id));
                    this.loading = false;
                })
                .catch((error) => {
                    console.error('Error deleting transaction activities:', error);
                    this.loading = false;
                    throw error;
                });
        });
    }

    private transactionActivitySaved(result: any): void {
        if (result && this.client && this.client.id) {
            this.loading = true;
            this.clientCRMRepo.saveTransactionActivity(this.client.id, result)
                .then((response: any) => {
                    // Reload the transaction activities from the backend
                    this.loadTransactions();
                    this.loading = false;
                })
                .catch((error) => {
                    console.error('Error saving transaction activity:', error);
                    this.loading = false;
                });
        }
    }

    private loadDropdownOptions(options: any): void {
        // Store all options in the single dropdownOptions object
        this.dropdownOptions = options;
    }
}

export const ClientDetailsBankingActivitiesComponent: angular.IComponentOptions = {
    template: require('./clientdetails.banking-activities.html'),
    controller: ClientDetailsBankingActivitiesController,
    controllerAs: '$ctrl',
    bindings: {
        client: '<',
        clientNumber: '<',
        isViewOnly: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/banking-activities/clientdetails.banking-activities.ts